// Painel de Histórico de Relatórios CLT × PJ — favoritos, tags, filtro, reemissão.
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ClipboardList, RotateCcw, Trash2, Users, Star, Tag, X, Plus } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/format";
import CltPjReportDialog from "@/components/CltPjReportDialog";
import MultiScenarioDialog from "@/components/MultiScenarioDialog";

export default function CltPjReportsHistoryPanel({ companyId }) {
  const [items, setItems] = useState(null);
  const [tagsAvailable, setTagsAvailable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reopen, setReopen] = useState(null);
  const [filterFavorite, setFilterFavorite] = useState(false);
  const [filterTag, setFilterTag] = useState("");
  const [editingTagsFor, setEditingTagsFor] = useState(null); // report id
  const [tagInput, setTagInput] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (companyId) params.company_id = companyId;
      if (filterFavorite) params.is_favorite = true;
      if (filterTag) params.tag = filterTag;
      const { data } = await api.get("/clt-pj/reports", { params });
      setItems(data.items || []);
      setTagsAvailable(data.tags_available || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setItems([]);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId, filterFavorite, filterTag]);

  const toggleFavorite = async (r) => {
    try {
      const { data } = await api.patch(`/clt-pj/reports/${r.id}`, { is_favorite: !r.is_favorite });
      setItems((prev) => prev.map((x) => x.id === r.id ? data : x));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveTags = async (r, newTags) => {
    try {
      const { data } = await api.patch(`/clt-pj/reports/${r.id}`, { tags: newTags });
      setItems((prev) => prev.map((x) => x.id === r.id ? data : x));
      setTagsAvailable((prev) => Array.from(new Set([...prev, ...newTags])).sort());
      setEditingTagsFor(null); setTagInput("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addTag = (r) => {
    const t = tagInput.trim();
    if (!t) return;
    const existing = r.tags || [];
    if (existing.includes(t)) { setTagInput(""); return; }
    saveTags(r, [...existing, t]);
  };

  const removeTag = (r, t) => saveTags(r, (r.tags || []).filter(x => x !== t));

  const reemit = async (r) => {
    try {
      const { data } = await api.get(`/clt-pj/reports/${r.id}`);
      setReopen({ kind: data.scenario_type === "multi" ? "multi" : "single", data });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (r) => {
    if (!window.confirm(`Excluir relatório "${r.title}"?`)) return;
    try {
      await api.delete(`/clt-pj/reports/${r.id}`);
      setItems((prev) => prev.filter(x => x.id !== r.id));
      toast.success("Excluído.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const favCount = useMemo(() => (items || []).filter(x => x.is_favorite).length, [items]);

  return (
    <div className="space-y-3" data-testid="clt-pj-history-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-[13px] font-semibold text-slate-800 flex items-center gap-1.5">
          <ClipboardList className="h-4 w-4 text-[#065F46]" />
          Histórico de Relatórios · CLT × PJ
        </h3>
        <div className="flex gap-1.5 items-center flex-wrap">
          <button className={`h-7 px-2.5 rounded-md text-[11px] flex items-center gap-1 border ${filterFavorite ? "bg-[#C5A059] text-[#1A231F] border-[#C5A059]" : "bg-white text-slate-700 border-slate-300"}`}
                  onClick={() => setFilterFavorite((v) => !v)}
                  data-testid="btn-filter-fav">
            <Star className={`h-3 w-3 ${filterFavorite ? "fill-current" : ""}`} /> Favoritos
          </button>
          {tagsAvailable.length > 0 && (
            <select value={filterTag} onChange={(e) => setFilterTag(e.target.value)}
                    className="h-7 px-2 text-[11px] rounded-md border border-slate-300 bg-white"
                    data-testid="filter-tag">
              <option value="">Todas as tags</option>
              {tagsAvailable.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          )}
          <Button size="sm" variant="outline" onClick={load} className="h-7 text-[11px]"
                  data-testid="btn-refresh-history">
            <RotateCcw className="h-3 w-3 mr-1" /> Atualizar
          </Button>
        </div>
      </div>

      {loading ? (
        <Skeleton className="h-28" />
      ) : items.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 bg-white p-6 text-center text-slate-500 text-[12px]"
             data-testid="history-empty">
          {filterFavorite || filterTag
            ? "Nenhum relatório com os filtros aplicados."
            : "Nenhum relatório salvo. Após gerar um relatório, clique em Salvar no Histórico."}
        </div>
      ) : (
        <div className="rounded-md border border-slate-200 bg-white divide-y divide-slate-100">
          {items.map((r) => (
            <div key={r.id} className="px-3 py-2 text-[12px]" data-testid={`history-row-${r.id}`}>
              <div className="flex items-center gap-2">
                <button onClick={() => toggleFavorite(r)}
                        className="h-6 w-6 flex items-center justify-center hover:bg-amber-50 rounded"
                        title={r.is_favorite ? "Remover dos favoritos" : "Marcar como favorito"}
                        data-testid={`fav-${r.id}`}>
                  <Star className={`h-4 w-4 ${r.is_favorite ? "text-[#C5A059] fill-[#C5A059]" : "text-slate-300"}`} />
                </button>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-medium text-slate-800 truncate">{r.title}</span>
                    {r.scenario_type === "multi" && (
                      <Badge variant="outline" className="h-4 px-1.5 text-[9px] border-[#0F3B6B] text-[#0F3B6B]">
                        <Users className="h-2.5 w-2.5 mr-0.5" /> Multi
                      </Badge>
                    )}
                    {(r.tags || []).map(t => (
                      <span key={t} className="inline-flex items-center gap-0.5 bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded text-[9.5px]"
                            data-testid={`tag-${r.id}-${t}`}>
                        <Tag className="h-2.5 w-2.5" /> {t}
                        <button onClick={() => removeTag(r, t)} className="ml-0.5 text-slate-400 hover:text-rose-500">
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </span>
                    ))}
                    {editingTagsFor === r.id ? (
                      <span className="inline-flex items-center gap-1">
                        <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(r); } if (e.key === "Escape") { setEditingTagsFor(null); setTagInput(""); } }}
                               placeholder="Nova tag · Enter"
                               className="h-5 text-[10.5px] w-[130px] px-1.5" autoFocus
                               data-testid={`tag-input-${r.id}`} />
                        <button onClick={() => addTag(r)} className="text-[#065F46] hover:text-[#054d38]">
                          <Plus className="h-3 w-3" />
                        </button>
                      </span>
                    ) : (
                      <button onClick={() => { setEditingTagsFor(r.id); setTagInput(""); }}
                              className="inline-flex items-center gap-0.5 text-[9.5px] text-slate-400 hover:text-[#065F46]"
                              data-testid={`add-tag-${r.id}`}>
                        <Plus className="h-2.5 w-2.5" /> tag
                      </button>
                    )}
                  </div>
                  <div className="text-[10.5px] text-slate-500">
                    {fmtDateTime(r.created_at)} · por {r.created_by_email || "—"}
                    {r.meta?.cargo && <> · {r.meta.cargo}</>}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => reemit(r)} className="h-7 text-[11px]"
                        data-testid={`reemit-${r.id}`}>
                  <RotateCcw className="h-3 w-3 mr-1" /> Reemitir
                </Button>
                <Button size="sm" variant="ghost" onClick={() => del(r)} className="h-7 w-7 p-0 text-rose-500"
                        data-testid={`del-${r.id}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="text-[10.5px] text-slate-500 flex gap-3">
        <span>Total: {(items || []).length}</span>
        <span>Favoritos: {favCount}</span>
        <span>Tags: {tagsAvailable.length}</span>
      </div>

      {reopen?.kind === "single" && (
        <CltPjReportDialog open onClose={() => setReopen(null)}
          result={reopen.data.result} inputs={reopen.data.inputs} meta={reopen.data.meta} />
      )}
      {reopen?.kind === "multi" && (
        <MultiScenarioDialog open onClose={() => setReopen(null)}
          baseInputs={reopen.data.inputs} meta={reopen.data.meta} preloaded={reopen.data.scenarios} />
      )}
    </div>
  );
}
